"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, MapPin, CircleAlert as AlertCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function RegionalPostsGrid() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border/50 shadow-sm">
            <AspectRatio ratio={4 / 3} className="bg-muted animate-pulse" />
            <CardContent className="p-4 space-y-3">
              <div className="h-4 w-1/3 bg-muted rounded animate-pulse" />
              <div className="h-4 w-full bg-muted rounded animate-pulse" />
              <div className="h-4 w-2/3 bg-muted rounded animate-pulse" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="size-8 text-destructive mb-4" />
        <p className="text-muted-foreground mb-4">Gagal memuat berita wilayah.</p>
        <Button variant="outline" onClick={refetch}>Coba Lagi</Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center py-12 text-center border rounded-xl border-dashed">
        <p className="text-muted-foreground">Belum ada berita untuk wilayah ini.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="group overflow-hidden border-border/50 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col">
          <Link to={post.link || "#"} className="block overflow-hidden">
            <AspectRatio ratio={4 / 3}>
              <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/generic_news_thumb.png"} alt={post.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" loading="lazy" />
            </AspectRatio>
          </Link>
          <CardContent className="p-4 sm:p-5 md:p-6 flex flex-col flex-1">
            <div className="flex items-center gap-3 text-xs sm:text-sm md:text-base text-muted-foreground mb-2">
              <div className="flex items-center gap-1 text-primary font-medium">
                <MapPin className="size-3" />
                <span>{post.categories?.[0]?.name || "Nasional"}</span>
              </div>
              <div className="flex items-center gap-1">
                <SafeHydrate><Calendar className="size-3" /></SafeHydrate>
                <span>{post.date ? new Date(post.date).toLocaleDateString('id-ID', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              }) : ''}</span>
              </div>
            </div>
            <Link to={post.link || "#"} className="group-hover:text-primary transition-colors">
              <h3 className="font-semibold text-foreground sm:text-lg md:text-xl line-clamp-2 leading-snug mb-2">
                {post.title}
              </h3>
            </Link>
            <div className="mt-auto pt-2">
              <Link to={post.link || "#"} className="text-xs font-medium text-primary hover:underline inline-flex items-center">
                Baca selengkapnya
              </Link>
            </div>
          </CardContent>
        </Card>)}
    </div>;
}
export default function RegionalNews() {
  const wp_query_params_nasional = {
    post_type: 'post',
    status: 'publish',
    per_page: 4,
    page: 1,
    order: "desc",
    orderby: "date"
  };
  const wp_query_nasional = new WP_Query(wp_query_params_nasional);
  const wp_query_params_garut = {
    post_type: 'post',
    status: 'publish',
    per_page: 4,
    page: 1,
    order: "desc",
    orderby: "date"
  };
  const wp_query_garut = new WP_Query(wp_query_params_garut);
  return <section id="wilayah" className="bg-background py-12 sm:py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <Tabs defaultValue="nasional" className="w-full">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 border-b pb-4">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-2">Berita Wilayah</h2>
              <p className="text-muted-foreground text-sm sm:text-base md:text-lg max-w-2xl">
                Pantau perkembangan dan peristiwa terkini dari berbagai daerah di sekitar Anda.
              </p>
            </div>
            <TabsList className="w-full md:w-auto grid grid-cols-2 md:flex h-auto p-1">
              <TabsTrigger value="nasional" className="py-2 text-xs sm:text-sm md:text-base">Nasional</TabsTrigger>
              <TabsTrigger value="garut" className="py-2 text-xs sm:text-sm md:text-base">Garut</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="nasional" className="mt-0 outline-none">
            <PostsProvider wp_query={{
            post_type: "post",
            status: ["publish"],
            per_page: 4,
            page: 1,
            order: "desc",
            orderby: "date"
          }}>
              <RegionalPostsGrid />
            </PostsProvider>
          </TabsContent>

          <TabsContent value="garut" className="mt-0 outline-none">
            <PostsProvider wp_query={{
            post_type: "post",
            status: ["publish"],
            per_page: 4,
            page: 1,
            order: "desc",
            orderby: "date"
          }}>
              <RegionalPostsGrid />
            </PostsProvider>
          </TabsContent>
        </Tabs>
      </div>
    </section>;
}